import useScrollHandler from '@marine/services/src/ui/useScrollHandler';
import styles from '@styles/Maker/filter.module.scss';
import { Anchor, Button, Dropdown, Grid, Input, type MenuProps } from 'antd';
import cn from 'classnames';
import { ChevronDown, Search } from 'lucide-react';
import React, { FC } from 'react';

export type AlphabetItem = {
  char: string;
  enabled: boolean;
};

type Props = {
  setNameQuery: (value: string) => void;
  alphabet: AlphabetItem[];
};

const MakerFilter: FC<Props> = ({ setNameQuery, alphabet }) => {
  const { xl } = Grid.useBreakpoint();

  const { Link } = Anchor;
  const { stickyHeader } = useScrollHandler();
  const dropdownMenuItems: MenuProps['items'] = alphabet.map((item) => ({
    key: item.char,
    disabled: !item.enabled,
    label: item.enabled ? (
      <a href={`#maker-${item.char.toLocaleUpperCase()}`} className={styles.anchorLink}>
        {item.char}
      </a>
    ) : (
      <span className={`${styles.anchorLink} ${styles.anchorLinkDisabled}`}>{item.char}</span>
    ),
  }));

  return (
    <div className={cn(styles.container, { [styles.isSticky]: stickyHeader })}>
      {!xl ? (
        <div className={styles.containerDropdown}>
          {/* Labels the dropdown beside it rather than heading a section — as an `<h3>` it sat between
              the page `<h1>` and the letter `<h2>`s and left a level gap on /maker (MDC-4221). */}
          <p className={styles.dropdownTitle}>Filter by letter</p>

          <Dropdown
            menu={{ items: dropdownMenuItems }}
            trigger={['click']}
            classNames={{ root: styles.dropdown }}
            getPopupContainer={(trigger: any) => trigger.parentNode}
          >
            <Button className={styles.dropdownButton}>
              Select letter... <ChevronDown size={16} />
            </Button>
          </Dropdown>
        </div>
      ) : (
        <Anchor affix={false} className={styles.containerAnchor} offsetTop={144}>
          {alphabet.map((itemGroup) => {
            if (itemGroup.enabled) {
              return (
                <Link
                  key={itemGroup.char}
                  href={`#maker-${itemGroup.char.toLocaleUpperCase()}`}
                  title={itemGroup.char}
                  className={styles.anchorLink}
                />
              );
            }

            return (
              <span key={itemGroup.char} className={`${styles.anchorLink} ${styles.anchorLinkDisabled}`}>
                {itemGroup.char}
              </span>
            );
          })}
        </Anchor>
      )}

      <div className={styles.containerSearch}>
        <Input
          placeholder="Search for a maker..."
          suffix={<Search size={16} />}
          onChange={(e) => setNameQuery(e.target.value)}
          className={styles.search}
          allowClear
        />
      </div>
    </div>
  );
};

export default MakerFilter;
